import { useState, useCallback, useRef } from "react";
import axios from "axios";
import API_URL from "../redux2";
import { getNDCAirShopping } from "../components/NDC/Services/AirShopping";
import { getAirArabiaAirShopping } from "../components/AirArabia/Service/AirShopping.airarbia";
import { getAmadeusAirShopping } from "../components/Amadeus/Services/AirShopping.amadeus";
import {
  withTimeout,
  normalizeSearchError,
  PROVIDER_TIMEOUT_MS,
} from "../utils/searchRequestUtils";

export const PROVIDER_STATUS = {
  PENDING: "pending",
  FULFILLED: "fulfilled",
  TIMED_OUT: "timed_out",
  FAILED: "failed",
  CANCELLED: "cancelled",
};

const noop = () => {};

function runProviderTask(providerKey, searchData) {
  const startMs = Date.now();
  let promise;

  switch (providerKey) {
    case "GDS":
      promise = axios.post(API_URL + "flight/search", searchData);
      break;
    case "NDC":
      promise = getNDCAirShopping(searchData, noop);
      break;
    case "AirArabia":
      promise = getAirArabiaAirShopping(searchData, noop);
      break;
    case "Amadeus":
      promise = getAmadeusAirShopping(searchData, noop);
      break;
    default:
      return Promise.reject(new Error(`Unknown provider: ${providerKey}`));
  }

  return withTimeout(promise, PROVIDER_TIMEOUT_MS, providerKey)
    .then((res) => {
      const durationMs = Date.now() - startMs;
      const data = providerKey === "GDS" ? res?.data : res;
      return {
        provider: providerKey,
        status: PROVIDER_STATUS.FULFILLED,
        data,
        durationMs,
        error: null,
      };
    })
    .catch((err) => {
      const durationMs = Date.now() - startMs;
      const normalized = normalizeSearchError(
        err?.original || err,
        providerKey,
        err?.timeout
      );
      return {
        provider: providerKey,
        status: err?.timeout
          ? PROVIDER_STATUS.TIMED_OUT
          : PROVIDER_STATUS.FAILED,
        data: null,
        durationMs,
        error: normalized,
      };
    });
}

function getEnabledProviders(channel, eligibility) {
  const { isEligibleForNDC, isEligibleForAirArabia, isEligibleForAmadeus } =
    eligibility;
  const providers = [];

  if (!channel || channel === "Travelport") {
    providers.push("GDS");
  }
  if (isEligibleForNDC && (!channel || channel === "NDC")) {
    providers.push("NDC");
  }
  if (isEligibleForAirArabia && (!channel || channel === "Air Arabia")) {
    providers.push("AirArabia");
  }
  if (isEligibleForAmadeus && (!channel || channel === "Amadeus")) {
    providers.push("Amadeus");
  }

  return providers;
}

export function useFlightSearchOrchestrator() {
  const [providerStatus, setProviderStatus] = useState({});
  const [loading, setLoading] = useState(false);
  /** True when the last run had no enabled providers (immediate no-op). */
  const [emptyProviderRun, setEmptyProviderRun] = useState(false);
  const requestIdRef = useRef(0);

  const runSearch = useCallback(
    async (searchData, eligibility, channel, onProviderResult) => {
      const requestId = ++requestIdRef.current;

      const providers = getEnabledProviders(channel, eligibility);
      if (providers.length === 0) {
        setEmptyProviderRun(true);
        setLoading(false);
        setProviderStatus({});
        return;
      }

      setEmptyProviderRun(false);
      setLoading(true);
      setProviderStatus(
        Object.fromEntries(
          providers.map((p) => [p, { status: PROVIDER_STATUS.PENDING }])
        )
      );

      const applyResult = (result) => {
        if (requestId !== requestIdRef.current) return; // Stale request
        const flightsCount = result.data
          ? getFlightsCount(result.provider, result.data)
          : 0;
        setProviderStatus((prev) => ({
          ...prev,
          [result.provider]: {
            status: result.status,
            durationMs: result.durationMs,
            flightsCount,
          },
        }));
        if (typeof window !== "undefined" && process.env.NODE_ENV !== "test") {
          console.debug("[FlightSearch]", {
            requestId,
            provider: result.provider,
            status: result.status,
            durationMs: result.durationMs,
            flightsCount,
          });
        }
        onProviderResult?.(result);
      };

      const tasks = providers.map((providerKey) =>
        runProviderTask(providerKey, searchData)
      );

      // Process each result as soon as it completes (incremental display)
      tasks.forEach((task) => {
        task.then((result) => applyResult(result));
      });

      // Wait for all to settle, then clear loading
      await Promise.allSettled(tasks);
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    },
    []
  );

  const retryProvider = useCallback(
    (providerKey, searchData, eligibility, channel, onProviderResult) => {
      const applyResult = (result) => {
        const flightsCount = result.data
          ? getFlightsCount(result.provider, result.data)
          : 0;
        setProviderStatus((prev) => ({
          ...prev,
          [result.provider]: {
            status: result.status,
            durationMs: result.durationMs,
            flightsCount,
          },
        }));
        onProviderResult?.(result);
      };

      setProviderStatus((prev) => ({
        ...prev,
        [providerKey]: { status: PROVIDER_STATUS.PENDING },
      }));

      runProviderTask(providerKey, searchData).then(applyResult);
    },
    []
  );

  return {
    runSearch,
    retryProvider,
    providerStatus,
    loading,
    emptyProviderRun,
  };
}

function getFlightsCount(provider, data) {
  switch (provider) {
    case "GDS":
      const gds = data?.LowFareSearchRsp?.AirPricingSolution;
      return Array.isArray(gds) ? gds.length : gds ? 1 : 0;
    case "NDC":
      const ndc = data?.OffersGroup?.AirlineOffers?.[0]?.AirlineOffer;
      return Array.isArray(ndc) ? ndc.length : 0;
    case "AirArabia":
      let count = 0;
      const aa = data?.ondWiseFlightCombinations;
      if (aa) {
        Object.values(aa).forEach((ond) => {
          Object.values(ond.dateWiseFlightCombinations || {}).forEach(
            (dv) => (count += dv.flightOptions?.length || 0)
          );
        });
      }
      return count;
    case "Amadeus":
      return Array.isArray(data?.data) ? data.data.length : 0;
    default:
      return 0;
  }
}

// Legacy hook shape for backward compatibility (e.g. RecentFlights2)
export const useFlightSearch = (searchData, isEligibleForNDC, setLoading) => {
  const { runSearch, loading, providerStatus } = useFlightSearchOrchestrator();
  const [state, setState] = useState({
    data: null,
    ndcData: null,
    flights: [],
    airPrice: [],
    error: null,
  });

  const searchFlight = useCallback(
    async (flightData) => {
      setLoading?.(true);
      try {
        const response = await axios.post(
          API_URL + "flight/search",
          flightData
        );
        setState((prev) => ({
          ...prev,
          data: response.data,
          airPrice: response.data?.LowFareSearchRsp?.AirPricingSolution,
          flights: response.data?.LowFareSearchRsp?.AirSegmentList?.AirSegment,
        }));
      } catch (error) {
        setState((prev) => ({ ...prev, error }));
      } finally {
        setLoading?.(false);
      }
    },
    [setLoading]
  );

  const searchNDC = useCallback(async () => {
    if (!isEligibleForNDC) return;
    try {
      const response = await getNDCAirShopping(searchData, setLoading);
      setState((prev) => ({ ...prev, ndcData: response }));
    } catch (error) {
      console.error("NDC search error:", error);
      setState((prev) => ({ ...prev, error }));
    }
  }, [isEligibleForNDC, searchData, setLoading]);

  return {
    ...state,
    loading: loading || state.loading,
    searchFlight,
    searchNDC,
    runSearch,
    providerStatus,
  };
};
